/**
 * SimpleCarousel - Carrousel modifié pour un défilement continu fluide avec boucle infinie,
 * et qui ralentit au survol.
 */
class SimpleCarousel {
  constructor(element, options = {}) {
    // Récupération des éléments
    this.carousel = element;
    if (!this.carousel) return;
    this.inner = element.querySelector('.carousel-inner');
    if (!this.inner) return;
    this.slides = Array.from(element.querySelectorAll('.carousel-slide'));
    if (!this.slides.length) return;

    // Options par défaut (les vitesses sont en px/s)
    this.options = {
      autoplaySpeed: 100,       // vitesse normale (px/s)
      hoverAutoplaySpeed: 40,     // vitesse au survol (px/s)
      gap: 30,
      visibleSlides: 4, // conservé pour compatibilité, mais non utilisé ici
      ...options
    };

    // État et variables d'animation
    this.offset = 0;
    this.slideWidth = 0;
    this.originalSlidesCount = 0;
    this.totalWidth = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startPosition = 0;
    this.isHovered = false;
    this.lastTimestamp = null;
    this.animationFrameId = null;

    // Liaison des méthodes
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    // Initialisation
    this.setupInfiniteLoop();
    this.calculateDimensions();
    this.attachEventListeners();
    this.setInitialPosition();
    this.startAnimation();
  }

  setupInfiniteLoop() {
    // Pour un défilement continu, on clone l'ensemble des slides et on les ajoute à la fin
    const originalCount = this.slides.length;
    this.originalSlidesCount = originalCount;
    this.slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('carousel-clone');
      this.inner.appendChild(clone);
    });
    // (Optionnel) Mise à jour du tableau des slides
    this.slides = Array.from(this.carousel.querySelectorAll('.carousel-slide'));
  }

  calculateDimensions() {
    if (!this.slides.length) return;
    // Largeurs variables (ratio propre à chaque image) : on additionne la
    // largeur réelle des slides d'origine pour obtenir la longueur de la boucle.
    const originals = this.slides.slice(0, this.originalSlidesCount);
    this.totalWidth = originals.reduce(
      (sum, slide) => sum + slide.offsetWidth + this.options.gap,
      0
    );
    // Pas de slide (boutons prev/next) basé sur la première jaquette
    this.slideWidth = originals[0].offsetWidth + this.options.gap;
  }

  setInitialPosition() {
    // Position initiale : offset à 0
    this.offset = 0;
    this.updateTransform();
  }

  updateTransform() {
    // Mise à jour du transform sans transition CSS (pour un défilement fluide)
    this.inner.style.transition = 'none';
    this.inner.style.transform = `translateX(${this.offset}px)`;
  }

  startAnimation() {
    this.lastTimestamp = null;
    const animate = (timestamp) => {
      if (!this.lastTimestamp) {
        this.lastTimestamp = timestamp;
      }
      const delta = timestamp - this.lastTimestamp;
      this.lastTimestamp = timestamp;

      if (!this.isDragging) {
        // Choix de la vitesse en fonction du survol
        const speed = this.isHovered ? this.options.hoverAutoplaySpeed : this.options.autoplaySpeed;
        // Mise à jour de l'offset (défilement vers la gauche → offset négatif)
        this.offset -= speed * (delta / 1000);

        // Lorsque l'offset atteint la largeur totale des slides d'origine,
        // on le réinitialise pour obtenir une boucle continue
        if (Math.abs(this.offset) >= this.totalWidth) {
          this.offset += this.totalWidth;
        }
        this.updateTransform();
      }
      this.animationFrameId = requestAnimationFrame(animate);
    };
    this.animationFrameId = requestAnimationFrame(animate);
  }

  stopAnimation() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  attachEventListeners() {
    // Événements de glisser (souris et tactile)
    this.inner.addEventListener('mousedown', this.handleDragStart);
    this.inner.addEventListener('touchstart', this.handleDragStart, { passive: false });
    window.addEventListener('mousemove', this.handleDragMove);
    window.addEventListener('touchmove', this.handleDragMove, { passive: false });
    window.addEventListener('mouseup', this.handleDragEnd);
    window.addEventListener('touchend', this.handleDragEnd);

    // Désactivation du drag par défaut sur les images
    this.carousel.querySelectorAll('img').forEach(img =>
      img.addEventListener('dragstart', e => e.preventDefault())
    );

    // Boutons de navigation (déplacement discret d'une slide, si désiré)
    const prevButton = this.carousel.querySelector('.carousel-prev');
    const nextButton = this.carousel.querySelector('.carousel-next');
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.offset += this.slideWidth;
        if (this.offset > 0) {
          this.offset -= this.totalWidth;
        }
        this.updateTransform();
      });
    }
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.offset -= this.slideWidth;
        if (Math.abs(this.offset) >= this.totalWidth) {
          this.offset += this.totalWidth;
        }
        this.updateTransform();
      });
    }

    // Gestion du survol pour ralentir le défilement
    this.carousel.addEventListener('mouseenter', this.handleMouseEnter);
    this.carousel.addEventListener('mouseleave', this.handleMouseLeave);

    // Ajustement lors du redimensionnement
    window.addEventListener('resize', this.handleResize);
    // Recalcul une fois toutes les images chargées (largeurs = ratio des images)
    window.addEventListener('load', this.handleResize);
  }

  removeEventListeners() {
    this.inner.removeEventListener('mousedown', this.handleDragStart);
    this.inner.removeEventListener('touchstart', this.handleDragStart);
    window.removeEventListener('mousemove', this.handleDragMove);
    window.removeEventListener('touchmove', this.handleDragMove);
    window.removeEventListener('mouseup', this.handleDragEnd);
    window.removeEventListener('touchend', this.handleDragEnd);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('load', this.handleResize);
    this.carousel.removeEventListener('mouseenter', this.handleMouseEnter);
    this.carousel.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  handleDragStart(e) {
    if (e.type === 'mousedown') e.preventDefault();
    this.isDragging = true;
    this.startX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    this.startPosition = this.offset;
    this.stopAnimation(); // Arrêter l'animation pendant le drag
  }

  handleDragMove(e) {
    if (!this.isDragging) return;
    const currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    const diff = currentX - this.startX;
    this.offset = this.startPosition + diff;
    // Ajuster l'offset pour rester dans la boucle infinie
    if (this.offset > 0) {
      this.offset -= this.totalWidth;
    } else if (Math.abs(this.offset) >= this.totalWidth) {
      this.offset += this.totalWidth;
    }
    this.updateTransform();
  }

  handleDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.startAnimation();
  }

  handleMouseEnter() {
    this.isHovered = true;
  }

  handleMouseLeave() {
    this.isHovered = false;
  }

  handleResize() {
    this.calculateDimensions();
    this.updateTransform();
  }

  destroy() {
    this.stopAnimation();
    this.removeEventListeners();
  }
}

/**
 * Initialisation des carousels
 */
document.addEventListener('DOMContentLoaded', () => {
  // 1. Carousel événements existant (SimpleCarousel)
  const carouselElement = document.querySelector('.carousel');
  if (carouselElement) {
    if (window.carouselInstance) window.carouselInstance.destroy();
    window.carouselInstance = new SimpleCarousel(carouselElement, {
      autoplaySpeed: 150,
      hoverAutoplaySpeed: 100,
      gap: 30,
      visibleSlides: 4,
    });
  }

  // 2. Carousel Samsung (CSS Scroll Snap simple)
  const samsungCarousel = document.querySelector('.carousel-samsung');
  const samsungContainer = samsungCarousel?.querySelector('.carousel-inner');
  if (samsungContainer) {
    const slides = samsungContainer.querySelectorAll('.carousel-slide');
    const prevButton = samsungCarousel.querySelector('.carousel-prev');
    const nextButton = samsungCarousel.querySelector('.carousel-next');

    if (slides.length > 0) {
      let currentIndex = 0;
      const intervalTime = 4000;
      let intervalId;
      let userInteracted = false;

      const scrollToSlide = (index) => {
        const slide = slides[index];
        if (slide) {
          samsungContainer.scrollTo({
            left: slide.offsetLeft,
            behavior: 'smooth'
          });
        }
      };

      const startAutoScroll = () => {
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(() => {
          if (userInteracted) {
             clearInterval(intervalId);
             return;
          }
          currentIndex = (currentIndex + 1) % slides.length;
          scrollToSlide(currentIndex);
        }, intervalTime);
      };

      const stopAutoScroll = () => {
        userInteracted = true;
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      // Navigation arrow handlers
      if (prevButton) {
        prevButton.addEventListener('click', () => {
          stopAutoScroll();
          currentIndex = (currentIndex - 1 + slides.length) % slides.length;
          scrollToSlide(currentIndex);
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          stopAutoScroll();
          currentIndex = (currentIndex + 1) % slides.length;
          scrollToSlide(currentIndex);
        });
      }

      // Start initially
      startAutoScroll();

      const interactionEvents = ['mousedown', 'touchstart', 'wheel', 'touchmove', 'keydown'];

      interactionEvents.forEach(event => {
        samsungContainer.addEventListener(event, stopAutoScroll, { passive: true, once: true });
      });

      samsungContainer.addEventListener('scroll', () => {
          const scrollLeft = samsungContainer.scrollLeft;
          const slideWidth = slides[0].clientWidth || 1;
          currentIndex = Math.round(scrollLeft / slideWidth);

      }, { passive: true });
    }
  }
});
